/**
 * Parameter Merge Utility
 *
 * Merges AI model parameter schema with admin overrides to create
 * a unified parameter list for the node settings panel.
 *
 * Features:
 * - Applies admin visibility/order/default overrides
 * - Filters hidden parameters
 * - Groups parameters (input/output/settings)
 * - Sorts by admin-configured order
 */

import type { ModelParameterOverrides, AdminParameterOverride, ParameterControlPolicy } from '@genieartinc/schemas';
import { CommonAiParametersUI } from '@genieartinc/schemas';

/** JSON Schema property definition for a model parameter */
interface JsonSchemaProperty {
  title?: string;
  description?: string;
  type?: string;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  maxLength?: number;
  enum?: unknown[];
  required?: boolean;
  /** Inspector section. Only basic/advanced are read; other groups stay on the override. */
  group?: 'basic' | 'advanced';
  /** Do not copy a shared default into this control. Unset must stay omitted. */
  omitDefault?: boolean;
  anyOf?: JsonSchemaProperty[];
}

/**
 * Normalize a JSON Schema property that may use anyOf/oneOf patterns.
 * fal.ai commonly uses: { anyOf: [{enum: [...], type: "string"}, {type: "null"}] }
 * or: { anyOf: [{type: "integer"}, {type: "null"}] } for optional params.
 * This extracts the non-null branch's type, enum, min, max into a flat structure.
 */
function normalizeAnyOf(paramDef: JsonSchemaProperty): JsonSchemaProperty {
  const variants = paramDef.anyOf || (paramDef as Record<string, unknown>).oneOf as JsonSchemaProperty[] | undefined;
  if (!variants || variants.length === 0) return paramDef;

  // Find the non-null branch (the "real" type definition)
  const realBranch = variants.find(
    (v) => v.type !== 'null' && !(v.type === 'string' && !v.enum && variants.some((o) => o.enum))
  ) || variants[0];

  // Find enum from any branch
  const enumBranch = variants.find((v) => v.enum && v.enum.length > 0);

  return {
    ...paramDef,
    type: paramDef.type || realBranch.type,
    enum: paramDef.enum || enumBranch?.enum,
    minimum: paramDef.minimum ?? realBranch.minimum,
    maximum: paramDef.maximum ?? realBranch.maximum,
    default: paramDef.default ?? realBranch.default,
  };
}

// ============================================================================
// TYPES
// ============================================================================

export interface MergedParameter {
  // Basic info
  name: string;
  label: string;
  description?: string;
  type: string; // 'string', 'number', 'integer', 'boolean', 'array', etc.

  // Grouping ('basic'/'advanced' from array-format schemas, 'settings' from legacy)
  group: string;
  isInput?: boolean;
  isOutput?: boolean;
  isMainOutput?: boolean;

  // Visibility
  hidden?: boolean;
  disabled?: boolean;

  // UI Component
  uiType: string; // 'slider', 'number', 'select', 'toggle', 'input', 'textarea', 'seed', etc.
  uiTypeOverride?: string;

  // Value constraints
  default?: unknown;
  defaultOverride?: unknown;
  minimum?: number;
  maximum?: number;
  minOverride?: number;
  maxOverride?: number;
  enum?: unknown[];
  enumOverride?: unknown[];

  // Input/Output config
  inputType?: string;
  outputType?: string;
  outputMode?: 'handle' | 'json';
  minHandles?: number;
  maxHandles?: number;

  // Ordering
  order?: number;
  originalKey?: string;

  // Validation
  validationType?: string;
  required?: boolean;

  // Policy (from CommonAiParametersUI)
  policy?: ParameterControlPolicy;

  // Override-only flag (param exists in overrides but not in schema)
  isOverrideOnly?: boolean;
}

// ============================================================================
// PARAMETER MERGE LOGIC
// ============================================================================

/**
 * Merge parameter schema with admin overrides
 *
 * @param parameterSchema - JSON Schema properties object from model
 * @param parameterOverrides - Admin overrides from database
 * @returns Array of merged parameters, sorted by order
 */
export function mergeParametersWithAdminOverride(
  parameterSchema: Record<string, JsonSchemaProperty> = {},
  parameterOverrides: ModelParameterOverrides = {}
): MergedParameter[] {
  const merged: MergedParameter[] = [];

  // Extract order arrays from overrides (if available)
  const inputOrder = (parameterOverrides.__inputOrder as string[]) || [];
  const outputOrder = (parameterOverrides.__outputOrder as string[]) || [];
  const settingsOrder = (parameterOverrides.__settingsOrder as string[]) || [];

  // Track which schema params we've processed (for override-only detection)
  const processedParams = new Set<string>();

  // Merge each parameter
  for (const [paramName, rawParamDef] of Object.entries(parameterSchema)) {
    // Skip internal order arrays
    if (paramName.startsWith('__')) continue;

    // Normalize anyOf/oneOf patterns (e.g. { anyOf: [{type:"integer"},{type:"null"}] })
    const paramDef = normalizeAnyOf(rawParamDef);

    processedParams.add(paramName);
    const override = parameterOverrides[paramName];
    const commonParam = CommonAiParametersUI[paramName];

    // Auto-detect group. A published LLM list can name basic vs advanced itself.
    const group = paramDef.group || detectGroup(override);

    // UI type priority: CommonAiParametersUI (curated) > sync-generated override > auto-detect
    // CommonAiParametersUI is authoritative for well-known parameters (image_size, aspect_ratio, etc.)
    const uiType = commonParam?.component || override?.uiTypeOverride || detectUIType(paramDef, paramName);

    // Resolve policy from CommonAiParametersUI
    const policy = commonParam?.policy;
    let hidden = override?.hidden || false;
    let disabled = override?.disabled || false;

    // Apply policy enforcement
    if (policy) {
      if (policy.visibility === 'hidden') hidden = true;
      if (policy.visibility === 'readonly' || !policy.userEditable) disabled = true;
    }

    // Build merged parameter
    const mergedParam: MergedParameter = {
      // Basic info (CommonAiParametersUI label takes priority for known params)
      name: paramName,
      label: commonParam?.label || paramDef.title || humanizeKey(paramName),
      description: commonParam?.description || paramDef.description,
      type: paramDef.type || 'string',

      // Grouping
      group,
      isInput: override?.isInput,
      isOutput: override?.isOutput,
      isMainOutput: override?.isMainOutput,

      // Visibility (policy-enforced)
      hidden,
      disabled,

      // UI Component
      uiType,
      uiTypeOverride: override?.uiTypeOverride,

      // Value constraints (fallback chain: admin override > schema > CommonAiParametersUI)
      // Schema values are model-specific and more accurate than generic CommonAiParametersUI ranges
      default: override?.defaultOverride !== undefined
        ? override.defaultOverride
        : paramDef.omitDefault
          ? undefined
          : (paramDef.default ?? commonParam?.defaultValue),
      defaultOverride: override?.defaultOverride,
      minimum: override?.minOverride !== undefined
        ? override.minOverride
        : (paramDef.minimum ?? commonParam?.defaultRange?.min),
      maximum: override?.maxOverride !== undefined
        ? override.maxOverride
        : (paramDef.maximum ?? commonParam?.defaultRange?.max),
      minOverride: override?.minOverride,
      maxOverride: override?.maxOverride,
      enum: override?.enumOverride !== undefined
        ? override.enumOverride
        : (paramDef.enum || commonParam?.commonValues),
      enumOverride: override?.enumOverride,

      // Input/Output config
      inputType: override?.inputType,
      outputType: override?.outputType,
      outputMode: override?.outputMode,
      minHandles: override?.minHandles,
      maxHandles: override?.maxHandles,

      // Ordering
      order: override?.order,
      originalKey: override?.originalKey,

      // Validation
      validationType: override?.validationType,
      required: paramDef.required || false,

      // Policy
      policy,
    };

    merged.push(mergedParam);
  }

  // Process override-only parameters (overrides without matching schema)
  for (const [paramName, override] of Object.entries(parameterOverrides)) {
    if (paramName.startsWith('__')) continue;
    if (processedParams.has(paramName)) continue;
    if (!override) continue;

    const commonParam = CommonAiParametersUI[paramName];
    const group = detectGroup(override);
    const syntheticDef: JsonSchemaProperty = {
      title: commonParam?.label,
      description: commonParam?.description,
      type: commonParam?.type === 'adaptive' || commonParam?.type === 'output' ? 'string' : commonParam?.type,
      default: commonParam?.defaultValue,
      minimum: commonParam?.defaultRange?.min,
      maximum: commonParam?.defaultRange?.max,
      enum: commonParam?.commonValues,
    };

    const uiType = override.uiTypeOverride || detectUIType(syntheticDef, paramName);

    merged.push({
      name: paramName,
      label: syntheticDef.title || humanizeKey(paramName),
      description: syntheticDef.description,
      type: syntheticDef.type || 'string',
      group,
      isInput: override.isInput,
      isOutput: override.isOutput,
      isMainOutput: override.isMainOutput,
      hidden: override.hidden || false,
      disabled: override.disabled || false,
      uiType,
      uiTypeOverride: override.uiTypeOverride,
      default: override.defaultOverride ?? syntheticDef.default,
      defaultOverride: override.defaultOverride,
      minimum: override.minOverride ?? syntheticDef.minimum,
      maximum: override.maxOverride ?? syntheticDef.maximum,
      minOverride: override.minOverride,
      maxOverride: override.maxOverride,
      enum: override.enumOverride ?? syntheticDef.enum,
      enumOverride: override.enumOverride,
      inputType: override.inputType,
      outputType: override.outputType,
      outputMode: override.outputMode,
      minHandles: override.minHandles,
      maxHandles: override.maxHandles,
      order: override.order,
      originalKey: override.originalKey,
      validationType: override.validationType,
      required: false,
      isOverrideOnly: true,
    });
  }

  // Sort by order within each group
  return sortParametersByOrder(merged, inputOrder, outputOrder, settingsOrder);
}

// ============================================================================
// GROUP DETECTION
// ============================================================================

function detectGroup(override?: AdminParameterOverride): 'input' | 'output' | 'settings' {
  if (override?.group) {
    return override.group;
  }

  if (override?.isInput) {
    return 'input';
  }

  if (override?.isOutput) {
    return 'output';
  }

  return 'settings';
}

// ============================================================================
// UI TYPE DETECTION
// ============================================================================

/**
 * Auto-detect UI component type from parameter schema
 */
function detectUIType(paramDef: JsonSchemaProperty, paramName: string): string {
  // Seed special case
  if (paramName.toLowerCase().includes('seed')) {
    return 'seed';
  }

  // Boolean → toggle
  if (paramDef.type === 'boolean') {
    return 'toggle';
  }

  // Enum → select
  if (paramDef.enum && paramDef.enum.length > 0) {
    return 'select';
  }

  // Number/Integer with min/max → slider or number
  if (paramDef.type === 'number' || paramDef.type === 'integer') {
    // If has min/max, default to slider (can be overridden to 'number')
    if (paramDef.minimum !== undefined && paramDef.maximum !== undefined) {
      return 'slider';
    }
    return 'number';
  }

  // String
  if (paramDef.type === 'string') {
    // Long strings → textarea
    if (paramDef.maxLength && paramDef.maxLength > 100) {
      return 'textarea';
    }
    // Prompt/negative_prompt → textarea
    if (paramName.toLowerCase().includes('prompt')) {
      return 'textarea';
    }
    return 'input';
  }

  // Array → multi-select (future)
  if (paramDef.type === 'array') {
    return 'multi-select';
  }

  // Default to input
  return 'input';
}

// ============================================================================
// PARAMETER SORTING
// ============================================================================

/**
 * Sort parameters by order within each group
 *
 * Uses admin-configured order arrays (__inputOrder, __outputOrder, __settingsOrder)
 * Falls back to parameter.order field
 */
function sortParametersByOrder(
  parameters: MergedParameter[],
  inputOrder: string[],
  outputOrder: string[],
  settingsOrder: string[]
): MergedParameter[] {
  return parameters.sort((a, b) => {
    // Sort by group first (input → output → settings)
    const groupOrder = { input: 0, output: 1, settings: 2 };
    const aGroupKey = a.group as keyof typeof groupOrder;
    const bGroupKey = b.group as keyof typeof groupOrder;
    const groupDiff = (groupOrder[aGroupKey] ?? 999) - (groupOrder[bGroupKey] ?? 999);
    if (groupDiff !== 0) return groupDiff;

    // Within group, use order array or order field
    let orderA = a.order ?? 999;
    let orderB = b.order ?? 999;

    // Use order array if available
    if (a.group === 'input' && inputOrder.length > 0) {
      orderA = inputOrder.indexOf(a.name);
      orderB = inputOrder.indexOf(b.name);
      // If not in order array, put at end
      if (orderA === -1) orderA = 9999;
      if (orderB === -1) orderB = 9999;
    } else if (a.group === 'output' && outputOrder.length > 0) {
      orderA = outputOrder.indexOf(a.name);
      orderB = outputOrder.indexOf(b.name);
      if (orderA === -1) orderA = 9999;
      if (orderB === -1) orderB = 9999;
    } else if (a.group === 'settings' && settingsOrder.length > 0) {
      orderA = settingsOrder.indexOf(a.name);
      orderB = settingsOrder.indexOf(b.name);
      if (orderA === -1) orderA = 9999;
      if (orderB === -1) orderB = 9999;
    }

    return orderA - orderB;
  });
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convert snake_case or camelCase to Human Readable
 */
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// ============================================================================
// FILTERS
// ============================================================================

/**
 * Filter parameters by group and visibility
 */
export function filterParametersByGroup(
  parameters: MergedParameter[],
  group: string,
  options: {
    includeHidden?: boolean;
    includeDisabled?: boolean;
  } = {}
): MergedParameter[] {
  return parameters.filter((param) => {
    // Group filter
    if (param.group !== group) return false;

    // Visibility filter
    if (!options.includeHidden && param.hidden) return false;

    return true;
  });
}

/**
 * Get visible settings parameters (for node settings panel)
 * Includes any group that is NOT input/output (e.g., 'settings', 'basic', 'advanced')
 */
export function getVisibleSettingsParameters(parameters: MergedParameter[]): MergedParameter[] {
  return parameters.filter((param) => {
    if (param.group === 'input' || param.group === 'output') return false;
    if (param.hidden) return false;
    return true;
  });
}

/**
 * Get input parameters (for node handles)
 */
export function getInputParameters(parameters: MergedParameter[]): MergedParameter[] {
  return filterParametersByGroup(parameters, 'input', {
    includeHidden: true, // Input handles always shown
  });
}

/**
 * Get output parameters (for node handles + JSON metadata)
 */
export function getOutputParameters(parameters: MergedParameter[]): MergedParameter[] {
  return filterParametersByGroup(parameters, 'output', {
    includeHidden: true, // Output handles always shown
  });
}

/**
 * Get parameters for API generation payload
 * (excludes hidden, disabled, and input/output groups)
 */
export function getGenerationParameters(parameters: MergedParameter[]): MergedParameter[] {
  return parameters.filter((param) => {
    // Only settings group
    if (param.group !== 'settings') return false;

    // Exclude hidden
    if (param.hidden) return false;

    // Exclude disabled (use default value instead)
    if (param.disabled) return false;

    return true;
  });
}
